import { useState, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useUrlTab } from '../../lib/useUrlTab.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchUserDetail,
  fetchUserTransactions,
  fetchUserBalanceHistory,
  fetchUserActivityLog,
  postRefund,
  fetchImpersonateTargets,
  startImpersonate,
  resetUser,
  resetUserPassword,
  suspendUser,
  issueUserCredits,
} from '../../api/admin.js';
import { nanoToUsd, nanoToUsdCompact, formatDateTime } from '../../lib/format.js';
import { Avatar } from '../../components/ui/Avatar.js';
import { Sparkline } from '../../components/charts/Sparkline.js';
import { MutationModal } from '../../components/ui/MutationModal.js';
import { TabsBar, type TabsBarTab } from '../../components/ui/TabsBar.js';
import { MemoryTab } from './MemoryTab.js';
import { SecurityTab } from './SecurityTab.js';

type TabId = 'overview' | 'activity' | 'security' | 'memory';
const TABS: ReadonlyArray<TabsBarTab<TabId>> = [
  { id: 'overview', label: 'Overview' },
  { id: 'activity', label: 'Activity' },
  { id: 'security', label: 'Seguridad' },
  { id: 'memory', label: 'Memory' },
];

export function UserDetailPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useUrlTab<TabId>('tab', TABS, 'overview');

  const detail = useQuery({
    queryKey: ['admin', 'user', id],
    queryFn: () => fetchUserDetail(id ?? ''),
    enabled: Boolean(id),
  });

  const tx = useQuery({
    queryKey: ['admin', 'user-tx', id],
    queryFn: () => fetchUserTransactions(id ?? '', { limit: 50 }),
    enabled: Boolean(id),
  });

  if (!id) return <div className="loading">Falta user id</div>;

  return (
    <>
      {detail.isPending ? (
        <>
          <div className="page-header">
            <div>
              <h1 className="page-title">User detail</h1>
              <p className="page-sub mono">{id}</p>
            </div>
          </div>
          <div className="loading">Cargando…</div>
        </>
      ) : detail.isError ? (
        <>
          <div className="page-header">
            <div>
              <h1 className="page-title">User detail</h1>
              <p className="page-sub mono">{id}</p>
            </div>
          </div>
          <div className="loading">Error: {detail.error.message}</div>
        </>
      ) : (
        <>
          <div
            className="page-header"
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-5)' }}
          >
            <Avatar email={detail.data.email} size="lg" />
            <div style={{ flex: 1 }}>
              <h1 className="page-title">{detail.data.email}</h1>
              <p className="page-sub mono">
                {detail.data.id} ·{' '}
                <span className="tt-tert">{detail.data.billingMode}</span> ·{' '}
                <span className="tt-tert">creado {formatDateTime(detail.data.createdAt)}</span>
              </p>
            </div>
            <span className={`badge ${detail.data.role === 'admin' ? 'b-info' : 'b-neutral'}`}>
              {detail.data.role}
            </span>
            <UserMutationsBar userId={id} email={detail.data.email} />
          </div>
          <div className="pad-7" data-testid="user-detail">
            <TabsBar tabs={TABS} active={tab} onChange={setTab} />
            {tab === 'activity' ? <ActivityTab userId={id} /> : null}
            {tab === 'security' ? <SecurityTab userId={id} /> : null}
            {tab === 'memory' ? <MemoryTab userId={id} /> : null}
            <div style={{ display: tab === 'overview' ? 'block' : 'none' }}>
            <section className="card" style={{ marginBottom: 24 }}>
              <header className="card-head">
                <h3 className="card-title">Identidad</h3>
              </header>
              <div className="pad-6">
              <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '8px 16px' }}>
                <dt className="tt-tert">Email</dt>
                <dd className="mono">{detail.data.email}</dd>
                <dt className="tt-tert">Role</dt>
                <dd><span className="badge b-info">{detail.data.role}</span></dd>
                <dt className="tt-tert">Name</dt>
                <dd>{detail.data.name ?? '—'}</dd>
                <dt className="tt-tert">Locale</dt>
                <dd className="mono">{detail.data.locale}</dd>
                <dt className="tt-tert">Timezone</dt>
                <dd className="mono">{detail.data.timezone}</dd>
                <dt className="tt-tert">Created</dt>
                <dd className="mono">{formatDateTime(detail.data.createdAt)}</dd>
                <dt className="tt-tert">Billing mode</dt>
                <dd className="mono">{detail.data.billingMode}</dd>
                <dt className="tt-tert">Balance</dt>
                <dd className="mono">{nanoToUsd(detail.data.creditsBalanceNano)}</dd>
                <dt className="tt-tert">Spend 7d</dt>
                <dd className="mono">{nanoToUsdCompact(detail.data.spend7dNano)}</dd>
              </dl>
              <RefundActions userId={id} />
            </div>
          </section>

          <section className="card" style={{ marginBottom: 24 }}>
            <header className="card-head">
              <h3 className="card-title">
                BYOK keys <small>{detail.data.byokKeys.length}</small>
              </h3>
            </header>
            {detail.data.byokKeys.length === 0 ? (
              <div className="empty">Sin keys</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Label</th>
                    <th>Created</th>
                    <th>Last used</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.byokKeys.map((k) => (
                    <tr key={`${k.providerId}-${k.createdAt}`}>
                      <td className="mono">{k.providerId}</td>
                      <td>{k.label ?? '—'}</td>
                      <td className="tt-tert mono">{formatDateTime(k.createdAt)}</td>
                      <td className="tt-tert mono">{k.lastUsedAt ? formatDateTime(k.lastUsedAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="card">
            <header className="card-head">
              <h3 className="card-title">
                Transactions <small>últimas 50</small>
              </h3>
            </header>
            {tx.isPending ? (
              <div className="loading">Cargando…</div>
            ) : tx.isError ? (
              <div className="loading">Error</div>
            ) : tx.data.items.length === 0 ? (
              <div className="empty">Sin transacciones</div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Kind</th>
                    <th className="r">Amount</th>
                    <th className="r">Balance after</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {tx.data.items.map((t) => (
                    <tr key={t.id}>
                      <td className="tt-tert mono">{formatDateTime(t.createdAt)}</td>
                      <td>
                        <span className="badge b-neutral">{t.kind}</span>
                      </td>
                      <td className="r mono">{nanoToUsd(t.amountNano)}</td>
                      <td className="r mono">{nanoToUsd(t.balanceAfterNano)}</td>
                      <td className="tt-sec">{t.notes ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <BalanceHistorySection userId={id} />
            </div>
          </div>
        </>
      )}
    </>
  );
}

function BalanceHistorySection({ userId }: { userId: string }): ReactElement {
  const q = useQuery({
    queryKey: ['admin', 'user', userId, 'balance-history'],
    queryFn: () => fetchUserBalanceHistory(userId),
  });
  return (
    <section className="card" style={{ marginTop: 24 }}>
      <header className="card-head">
        <h3 className="card-title">Balance history (30d)</h3>
      </header>
      <div className="pad-6">
        {q.isPending ? (
          <div className="loading">Cargando…</div>
        ) : q.isError ? (
          <div className="loading">Error: {(q.error).message}</div>
        ) : q.data.buckets.length === 0 ? (
          <div className="empty">Sin datos</div>
        ) : (
          <>
            <Sparkline
              values={q.data.buckets.map((b) => Number(BigInt(b.balanceNano) / 100_000_000n))}
              className="spark spark-lg"
            />
            <table className="table" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Day</th>
                  <th className="r">Δ</th>
                  <th className="r">Balance EOD</th>
                </tr>
              </thead>
              <tbody>
                {q.data.buckets.slice(-7).reverse().map((b) => (
                  <tr key={b.day}>
                    <td className="mono tt-tert">{b.day}</td>
                    <td className="r mono">{nanoToUsd(b.deltaNano)}</td>
                    <td className="r mono">{nanoToUsd(b.balanceNano)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </section>
  );
}

function ActivityTab({ userId }: { userId: string }): ReactElement {
  const q = useQuery({
    queryKey: ['admin', 'user', userId, 'activity-log'],
    queryFn: () => fetchUserActivityLog(userId, { limit: 50 }),
  });
  return (
    <section className="card">
      <header className="card-head">
        <h3 className="card-title">Activity timeline</h3>
      </header>
      {q.isPending ? (
        <div className="loading">Cargando…</div>
      ) : q.isError ? (
        <div className="loading">Error: {(q.error).message}</div>
      ) : q.data.items.length === 0 ? (
        <div className="empty">Sin actividad</div>
      ) : (
        <ul className="activity-feed" data-testid="activity-feed">
          {q.data.items.map((item, idx) => (
            <li key={`${item.refId ?? idx}-${item.at}`} className="activity-item">
              <span className="activity-when tt-tert mono">{formatDateTime(item.at)}</span>
              <span className={`badge b-${badgeToneFor(item.kind)}`}>{item.kind}</span>
              <span className="activity-summary">{item.summary}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function badgeToneFor(kind: string): string {
  if (kind.startsWith('credit_')) return 'info';
  if (kind === 'admin_action') return 'warn';
  if (kind === 'session_started' || kind === 'session_ended') return 'neutral';
  return 'neutral';
}

interface UserMutationsBarProps {
  userId: string;
  email: string;
}

function UserMutationsBar({ userId, email }: UserMutationsBarProps): ReactElement {
  const qc = useQueryClient();
  const [open, setOpen] = useState<
    null | 'impersonate' | 'reset' | 'reset-password' | 'suspend' | 'issue-credits'
  >(null);
  const [resetToken, setResetToken] = useState<string | null>(null);

  // Apps destino del impersonate (selector). Sólo se cargan al abrir el modal.
  const targetsQuery = useQuery({
    queryKey: ['admin', 'impersonate-targets'],
    queryFn: fetchImpersonateTargets,
    enabled: open === 'impersonate',
    staleTime: 5 * 60_000,
  });
  const targets = targetsQuery.data ?? [];
  const [impApp, setImpApp] = useState('');
  const selectedApp = impApp || targets[0]?.id || '';

  // Link de acceso generado (un solo uso). NO se auto-abre: mostramos el link
  // para que el operador elija dónde abrirlo — en local conviene pegarlo en una
  // ventana de incógnito (cookie store separado) para no pisar la sesión del BO.
  const [impLink, setImpLink] = useState<string | null>(null);
  const [impCopied, setImpCopied] = useState(false);
  const impMut = useMutation({
    mutationFn: (app: string) => startImpersonate(userId, app),
    onSuccess: (data) => {
      setImpLink(data.redeemUrl);
      setImpCopied(false);
    },
  });
  const resetMut = useMutation({
    mutationFn: () => resetUser(userId),
    onSuccess: () => {
      setOpen(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-tx', userId] });
    },
  });
  const rstMut = useMutation({
    mutationFn: () => resetUserPassword(userId),
    onSuccess: (data) => {
      setResetToken(data.token);
    },
  });
  const susMut = useMutation({
    mutationFn: (reason: string) => suspendUser(userId, reason),
    onSuccess: () => {
      setOpen(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
    },
  });
  const [creditsUsd, setCreditsUsd] = useState('');
  const credMut = useMutation({
    mutationFn: (reason: string) => {
      const usd = Number.parseFloat(creditsUsd);
      if (!Number.isFinite(usd) || usd <= 0) throw new Error('Monto inválido');
      const nano = BigInt(Math.round(usd * 1e8));
      return issueUserCredits(userId, nano.toString(), reason);
    },
    onSuccess: () => {
      setOpen(null);
      setCreditsUsd('');
      void qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-tx', userId] });
    },
  });

  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen('impersonate')}
        data-testid="btn-impersonate"
      >
        Impersonate
      </button>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen('reset-password')}
        data-testid="btn-reset-password"
      >
        Reset password
      </button>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen('issue-credits')}
        data-testid="btn-issue-credits"
      >
        Issue credits
      </button>
      <button
        type="button"
        className="btn btn-danger"
        onClick={() => setOpen('suspend')}
        data-testid="btn-suspend"
      >
        Suspend
      </button>
      <button
        type="button"
        className="btn btn-danger"
        onClick={() => setOpen('reset')}
        data-testid="btn-reset"
      >
        Resetear
      </button>

      <MutationModal
        open={open === 'impersonate'}
        title={`Impersonate ${email}`}
        description={
          impLink ? (
            <>
              <strong>Link de acceso generado</strong> — un solo uso, vence en 2 min.
              <pre className="code-block" style={{ marginTop: 8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {impLink}
              </pre>
              <p className="tt-tert" style={{ marginTop: 8 }}>
                Abrilo para entrar a la app como el user. <strong>En local</strong>, pegalo en una
                ventana de <strong>incógnito</strong> (cookie store separado) para no pisar tu sesión
                del backoffice. En prod no hace falta: los dominios están aislados.
              </p>
            </>
          ) : (
            'Genera un link de un solo uso que abre la app elegida logueada como este user. Tu sesión del backoffice no se toca.'
          )
        }
        confirmLabel={impLink ? 'Abrir en pestaña nueva' : 'Generar link'}
        tone="warn"
        isPending={impMut.isPending}
        errorMessage={
          impMut.error instanceof Error
            ? impMut.error.message
            : targetsQuery.error instanceof Error
              ? targetsQuery.error.message
              : null
        }
        onCancel={() => {
          setOpen(null);
          setImpLink(null);
        }}
        onConfirm={() => {
          if (impLink) {
            window.open(impLink, '_blank', 'noopener');
            setImpLink(null);
            setOpen(null);
          } else if (selectedApp) {
            impMut.mutate(selectedApp);
          }
        }}
      >
        {impLink ? (
          <button
            type="button"
            className="btn"
            data-testid="impersonate-copy"
            onClick={() => {
              void navigator.clipboard?.writeText(impLink);
              setImpCopied(true);
            }}
          >
            {impCopied ? 'Copiado ✓' : 'Copiar link'}
          </button>
        ) : (
          <label className="mm-field">
            <span className="mm-label">App destino</span>
            <select
              value={selectedApp}
              onChange={(e) => setImpApp(e.target.value)}
              disabled={targetsQuery.isLoading || targets.length === 0}
              data-testid="impersonate-app"
            >
              {targetsQuery.isLoading ? (
                <option value="">Cargando…</option>
              ) : targets.length === 0 ? (
                <option value="">No hay apps configuradas</option>
              ) : (
                targets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))
              )}
            </select>
          </label>
        )}
      </MutationModal>

      <MutationModal
        open={open === 'reset'}
        title={`Resetear ${email}`}
        description="Borra TODO el historial, memoria, progreso de inglés, correcciones, sesiones y billing de este user. El balance vuelve a USD 10. El login (email, contraseña, rol) se conserva. Irreversible."
        confirmLabel="Resetear cuenta"
        tone="danger"
        requiresLiteralConfirm={email}
        isPending={resetMut.isPending}
        errorMessage={resetMut.error instanceof Error ? resetMut.error.message : null}
        onCancel={() => setOpen(null)}
        onConfirm={() => resetMut.mutate()}
      />

      <MutationModal
        open={open === 'reset-password'}
        title={`Reset password — ${email}`}
        description={
          resetToken ? (
            <>
              <strong>Link de reset generado.</strong> Compartilo out-of-band con el user:
              <pre className="code-block" style={{ marginTop: 8 }}>{resetToken}</pre>
              <p className="tt-tert">Token vence en 30 min.</p>
            </>
          ) : (
            'Vamos a generar un token de reset con TTL 30 min. El admin se lo entrega al user out-of-band.'
          )
        }
        confirmLabel={resetToken ? 'Cerrar' : 'Generar token'}
        tone="warn"
        isPending={rstMut.isPending}
        errorMessage={rstMut.error instanceof Error ? rstMut.error.message : null}
        onCancel={() => {
          setOpen(null);
          setResetToken(null);
        }}
        onConfirm={() => {
          if (resetToken) {
            setResetToken(null);
            setOpen(null);
          } else {
            rstMut.mutate();
          }
        }}
      />

      <MutationModal
        open={open === 'suspend'}
        title={`Suspend ${email}`}
        description="El user no podrá iniciar sesión hasta que se reactive. La acción queda auditada."
        confirmLabel="Suspender"
        tone="danger"
        requiresReason
        requiresLiteralConfirm="SUSPEND"
        isPending={susMut.isPending}
        errorMessage={susMut.error instanceof Error ? susMut.error.message : null}
        onCancel={() => setOpen(null)}
        onConfirm={(reason) => susMut.mutate(reason)}
      />

      <MutationModal
        open={open === 'issue-credits'}
        title={`Issue credits — ${email}`}
        confirmLabel="Otorgar"
        tone="primary"
        requiresReason
        isPending={credMut.isPending}
        errorMessage={credMut.error instanceof Error ? credMut.error.message : null}
        onCancel={() => setOpen(null)}
        onConfirm={(reason) => credMut.mutate(reason)}
      >
        <label className="mm-field">
          <span className="mm-label">Monto USD (máx 1000)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={creditsUsd}
            onChange={(e) => setCreditsUsd(e.target.value)}
            data-testid="issue-credits-amount"
          />
        </label>
      </MutationModal>
    </>
  );
}

function RefundActions({ userId }: { userId: string }): ReactElement {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [amountUsd, setAmountUsd] = useState('');
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: () => {
      const usd = Number.parseFloat(amountUsd);
      if (!Number.isFinite(usd) || usd <= 0) throw new Error('Monto inválido');
      const nano = BigInt(Math.round(usd * 1e8));
      return postRefund({ userId, amountNano: nano.toString(), reason });
    },
    onSuccess: () => {
      setOpen(false);
      setAmountUsd('');
      setReason('');
      void qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-tx', userId] });
    },
  });
  return (
    <div style={{ marginTop: 16 }}>
      {!open ? (
        <button type="button" className="btn primary" onClick={() => setOpen(true)} data-testid="refund-open">
          Refund credits
        </button>
      ) : (
        <div className="form-grid" data-testid="refund-form">
          <label>
            Monto USD
            <input
              value={amountUsd}
              onChange={(e) => setAmountUsd(e.target.value)}
              placeholder="1.00"
            />
          </label>
          <label className="form-row-2">
            Reason
            <input value={reason} onChange={(e) => setReason(e.target.value)} required />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn primary"
              disabled={mut.isPending || !reason || !amountUsd}
              onClick={() => {
                if (confirm(`Aplicar refund de $${amountUsd}? Acción auditada.`)) mut.mutate();
              }}
            >
              {mut.isPending ? 'Aplicando…' : 'Confirmar refund'}
            </button>
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              Cancelar
            </button>
          </div>
          {mut.isError && <p className="error">{(mut.error).message}</p>}
        </div>
      )}
    </div>
  );
}
